import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/TranslationContext';
import { useAppContext } from '@/contexts/AppContext';
import { Search, Save } from 'lucide-react';

const TranslationEditor: React.FC = () => {
  const { translations, t, updateTranslation } = useTranslation();
  const { currentUser } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [editedTranslations, setEditedTranslations] = useState<{[key: string]: string}>({});

  // Only allow admin users to access this component
  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-gray-500">
              {t('error')}: Access denied. Admin privileges required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredTranslations = Object.entries(translations).filter(
    ([key, value]) =>
      key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTranslationChange = (key: string, value: string) => {
    setEditedTranslations(prev => ({ ...prev, [key]: value }));
  };

  const saveTranslation = (key: string) => {
    const newValue = editedTranslations[key];
    if (newValue !== undefined) {
      updateTranslation(key, newValue);
      setEditedTranslations(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const saveAllTranslations = () => {
    Object.entries(editedTranslations).forEach(([key, value]) => {
      updateTranslation(key, value);
    });
    setEditedTranslations({});
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>{t('translation.editor')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Save All Button */}
          {Object.keys(editedTranslations).length > 0 && (
            <div className="flex justify-end">
              <Button onClick={saveAllTranslations} className="flex items-center space-x-2">
                <Save className="h-4 w-4" />
                <span>{t('save')} {t('actions')}</span>
              </Button>
            </div>
          )}

          {/* Translation List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredTranslations.map(([key, value]) => {
              const currentEdit = editedTranslations[key];
              const displayValue = currentEdit !== undefined ? currentEdit : value;
              const hasChanges = currentEdit !== undefined && currentEdit !== value;

              return (
                <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border rounded-lg">
                  <div className="md:col-span-1">
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t('translation.key')}
                    </label>
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                      {key}
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <label className="text-sm font-medium text-gray-700 block mb-1">
                      {t('translation.value')}
                    </label>
                    <Input
                      value={displayValue}
                      onChange={(e) => handleTranslationChange(key, e.target.value)}
                      className={hasChanges ? 'border-yellow-400 bg-yellow-50' : ''}
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button
                      onClick={() => saveTranslation(key)}
                      disabled={!hasChanges}
                      size="sm"
                      className="w-full md:w-auto"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {t('save')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredTranslations.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('noData')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TranslationEditor;